import React, { useState, useCallback, memo, useEffect, useRef } from "react";
import instance, { apiEncryptRequest, decryptData } from "../../Api";
import { useAuth } from "../../AuthContext";
import toast from "react-hot-toast";
import AppToast from "../../AppToast";

// ─── Confetti particle helper ───────────────────────────────────────────────
const COLORS = ["#e97730", "#f4a35a", "#34C78A", "#154e7a", "#FFD700", "#FF6B6B", "#A855F7"];
const rand = (min, max) => Math.random() * (max - min) + min;

const ConfettiCanvas = memo(({ active }) => {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);
  const particles = useRef([]);

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width  = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    particles.current = Array.from({ length: 80 }, () => ({
      x:     rand(0, canvas.width),
      y:     rand(-20, 0),
      r:     rand(5, 10),
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      rot:   rand(0, 360),
      vx:    rand(-3, 3),
      vy:    rand(3, 8),
      vr:    rand(-5, 5),
      shape: Math.random() > 0.5 ? "rect" : "circle",
      w:     rand(6, 14),
      h:     rand(4, 8),
      alpha: 1,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let alive = false;
      particles.current.forEach((p) => {
        p.x   += p.vx;
        p.y   += p.vy;
        p.rot += p.vr;
        p.vy  += 0.15;
        p.alpha -= 0.008;
        if (p.alpha > 0) alive = true;
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.alpha);
        ctx.fillStyle   = p.color;
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rot * Math.PI) / 180);
        if (p.shape === "circle") {
          ctx.beginPath();
          ctx.arc(0, 0, p.r, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }
        ctx.restore();
      });
      if (alive) animRef.current = requestAnimationFrame(draw);
      else ctx.clearRect(0, 0, canvas.width, canvas.height);
    };
    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [active]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0, left: 0,
        width: "100%", height: "100%",
        pointerEvents: "none",
        zIndex: 10,
        borderRadius: 40,
      }}
    />
  );
});

// ─── Ripple button ──────────────────────────────────────────────────────────
const RippleButton = memo(({ onClick, disabled, children }) => {
  const [ripples, setRipples] = useState([]);
  const [pressed, setPressed] = useState(false);

  const handleClick = useCallback((e) => {
    if (disabled) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const id = Date.now();
    setRipples((r) => [...r, { x, y, id }]);
    setTimeout(() => setRipples((r) => r.filter((rp) => rp.id !== id)), 700);
    setPressed(true);
    setTimeout(() => setPressed(false), 150);
    if (onClick) onClick(e);
  }, [onClick, disabled]);

  return (
    <button
      onClick={handleClick}
      disabled={disabled}
      style={{
        position: "relative",
        overflow: "hidden",
        width: "100%",
        maxWidth: 420,
        height: 50,                   /* ← fixed height matches input */
        borderRadius: 40,
        background: disabled
          ? "rgba(233,119,48,0.5)"
          : "linear-gradient(90deg, #e97730, #f4a35a)",
        color: "white",
        border: "none",
        fontSize: 16,
        fontWeight: 700,
        letterSpacing: "0.06em",
        cursor: disabled ? "not-allowed" : "pointer",
        boxShadow: pressed
          ? "0 2px 6px rgba(233,119,48,0.3)"
          : "0 4px 20px rgba(233,119,48,0.5)",
        transform: pressed ? "scale(0.97)" : "scale(1)",
        transition: "transform 0.12s, box-shadow 0.12s",
        userSelect: "none",
      }}
    >
      {ripples.map(({ x, y, id }) => (
        <span
          key={id}
          style={{
            position: "absolute",
            left: x, top: y,
            width: 0, height: 0,
            background: "rgba(255,255,255,0.4)",
            borderRadius: "50%",
            transform: "translate(-50%,-50%)",
            animation: "ripple-anim 0.7s ease-out forwards",
            pointerEvents: "none",
          }}
        />
      ))}
      {children}
    </button>
  );
});

// ─── CouponBox ──────────────────────────────────────────────────────────────
const CouponBox = memo(({ orderAmount, isMobile, onApply, onRemove }) => {
  const { smuser } = useAuth();

  const [code,      setCode]      = useState("");
  const [applied,   setApplied]   = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [bounceIn,  setBounceIn]  = useState(false);

  const showToast = useCallback((msg) =>
    toast((t) => <AppToast id={t.id} message={msg} isMobile={isMobile} />), [isMobile]);

  const handleApply = useCallback(async () => {
    if (!code.trim()) { showToast("Enter coupon code"); return; }
    if (!orderAmount || orderAmount <= 0) { showToast("Select a plan first"); return; }
    setLoading(true);
    try {
      const enc  = await apiEncryptRequest({ user_id: smuser.id, coupon_code: code.trim(), order_amount: orderAmount });
      const res  = await instance.post("/coupon/check", enc);
      const data = await decryptData(res.data);
      if (data.valid) {
        setApplied(data);
        onApply({ couponCode: data.coupon_code, discount: data.discount, finalAmount: data.final_amount });
        showToast(data.message);
        setCelebrate(true);
        setBounceIn(true);
        setTimeout(() => setCelebrate(false), 2500);
        setTimeout(() => setBounceIn(false), 600);
      } else {
        showToast(data.message || "Invalid coupon");
      }
    } catch { showToast("Something went wrong. Try again."); }
    setLoading(false);
  }, [code, orderAmount, smuser?.id, onApply, showToast]);

  const handleRemove = useCallback(async () => {
    setApplied(null); setCode(""); onRemove();
    try {
      const enc = await apiEncryptRequest({ user_id: smuser.id });
      await instance.post("/coupon/remove", enc);
    } catch {}
  }, [smuser?.id, onRemove]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter") handleApply();
  }, [handleApply]);

  return (
    <>
      <style>{`
        @keyframes ripple-anim {
          to { width: 300px; height: 300px; opacity: 0; }
        }
        @keyframes bounce-in {
          0%   { transform: scale(0.4); opacity: 0; }
          55%  { transform: scale(1.15); opacity: 1; }
          75%  { transform: scale(0.92); }
          90%  { transform: scale(1.04); }
          100% { transform: scale(1); }
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(52,199,138,0.5); }
          50%       { box-shadow: 0 0 0 10px rgba(52,199,138,0); }
        }
        @keyframes shimmer {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes float-emoji {
          0%   { transform: translateY(0) scale(1); opacity: 1; }
          100% { transform: translateY(-60px) scale(1.4); opacity: 0; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        /* placeholder centered */
        .coupon-input::placeholder {
          color: rgba(255,255,255,0.7);
          text-align: center;
        }
      `}</style>

      <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>

        {applied ? (
          /* ── Applied state ── */
          <div style={{
            width: "100%", maxWidth: 420,
            display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
            position: "relative",
          }}>
            <ConfettiCanvas active={celebrate} />

            {/* Floating emojis */}
            {celebrate && ["🎉","✨","🎊","💰","🏷"].map((em, i) => (
              <span key={i} style={{
                position: "absolute",
                top: "10%",
                left: `${15 + i * 18}%`,
                fontSize: 22,
                animation: `float-emoji 1.2s ease-out ${i * 0.15}s forwards`,
                pointerEvents: "none",
                zIndex: 20,
              }}>{em}</span>
            ))}

            {/* Applied pill */}
            <div style={{
              width: "100%",
              height: 50,             /* ← same fixed height */
              display: "flex", alignItems: "center", justifyContent: "space-between",
              background: "linear-gradient(90deg, #0d3d2e, #155c43)",
              borderRadius: 40,
              padding: "0 10px 0 20px",
              boxShadow: "0 2px 16px rgba(52,199,138,0.3)",
              animation: bounceIn
                ? "bounce-in 0.55s cubic-bezier(0.36,0.07,0.19,0.97) both, pulse-glow 1.5s ease 0.6s 2"
                : "pulse-glow 1.5s ease 0s 1",
              position: "relative",
              overflow: "hidden",
            }}>
              {celebrate && (
                <div style={{
                  position: "absolute", inset: 0,
                  background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.15) 50%, transparent 60%)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 1.2s ease 0.3s 2",
                  borderRadius: 40,
                  pointerEvents: "none",
                }} />
              )}
              <span style={{
                color: "#34C78A", fontWeight: 800, fontSize: 15,
                letterSpacing: "0.12em", textTransform: "uppercase", zIndex: 1,
              }}>
                ✓ {applied.coupon_code}
              </span>
              <button onClick={handleRemove} style={{
                background: "transparent",
                color: "#F06292",
                border: "1.5px solid rgba(240,98,146,0.5)",
                borderRadius: 30, padding: "5px 16px",
                fontSize: 12, fontWeight: 700, cursor: "pointer",
                letterSpacing: "0.05em", zIndex: 1,
                transition: "all 0.2s",
              }}>
                Remove
              </button>
            </div>

            {/* Savings */}
            <div style={{
              fontSize: 13, fontWeight: 800, color: "#34C78A",
              animation: bounceIn ? "bounce-in 0.5s 0.2s both" : "none",
            }}>
              🎉 You save ₹{applied.discount}
            </div>
          </div>

        ) : (
          /* ── Input state ── */
          <div style={{ width: "100%", maxWidth: 420, display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>

            {/* Input pill — same height 50px as button */}
            <div style={{
              width: "100%",
              height: 50,             /* ← matches button height */
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(90deg, #154e7a, #1a6399)",
              borderRadius: 40,
              padding: "0 20px",
              boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
              boxSizing: "border-box",
            }}>
              <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 16, marginRight: 8, flexShrink: 0 }}>🏷</span>
              <input
                className="coupon-input"
                type="text"
                placeholder="ENTER COUPON CODE"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={handleKeyDown}
                maxLength={30}
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  color: "white",
                  fontSize: 14,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  caretColor: "white",
                  textAlign: "center",   /* ← centered text */
                  width: "100%",
                }}
              />
            </div>

            {/* Ripple Apply button */}
            <RippleButton onClick={handleApply} disabled={loading}>
              {loading ? (
                <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  <span style={{
                    width: 16, height: 16,
                    border: "2px solid rgba(255,255,255,0.4)",
                    borderTop: "2px solid white",
                    borderRadius: "50%",
                    display: "inline-block",
                    animation: "spin 0.7s linear infinite",
                  }} />
                  Checking...
                </span>
              ) : "Apply Coupon"}
            </RippleButton>
          </div>
        )}
      </div>
    </>
  );
});

export default CouponBox;